import { useId, type MouseEvent, type SyntheticEvent } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { SpreadsheetRangeAction } from "./rangeAction";
import styles from "./SpreadsheetRangeActionButton.module.scss";

const stopPropagation = (event: SyntheticEvent) => event.stopPropagation();

/**
 * The button on the selected range's top-right cell (see `rangeAction`). A
 * click runs the action; a press and move drags the range out instead.
 * Mousedowns and double clicks stay on it, so the grid doesn't start a range
 * selection or an edit under it.
 */
export const SpreadsheetRangeActionButton = ({
    action,
}: {
    action: SpreadsheetRangeAction;
}) => {
    const id = useId();
    const { listeners, setNodeRef, isDragging } = useDraggable({
        id: `range-action:${id}`,
        data: action.dragData,
    });
    const handleClick = (event: MouseEvent) => {
        event.stopPropagation();
        action.onClick();
    };
    // Pointer-only: the command bar button is the keyboard path.
    return (
        <Tooltip content={action.label} disabled={isDragging}>
            <button
                type="button"
                ref={setNodeRef}
                {...listeners}
                tabIndex={-1}
                aria-label={action.label}
                className={styles.button}
                onMouseDown={stopPropagation}
                onClick={handleClick}
                onDoubleClick={stopPropagation}
            >
                {action.icon}
            </button>
        </Tooltip>
    );
};
